'use client';

import { c as _c } from "react/compiler-runtime";
import type { DesktopHandoffRouteFamily } from '@/lib/desktop-handoff/constants';
import type { OpenInAppDecision } from '@/lib/desktop-handoff/open-in-app';
import { X } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useRef, useState } from 'react';
import { useDesktopOpenPromptStore } from './desktop-open-prompt-store';
import { HandoffInterstitial } from './handoff-interstitial';
import { useToDesktop } from '@/components/providers/todesktop-auth-provider';
import { Button } from '@/components/ui/button';
import { useProfile } from '@/data/profile/use-profile.client';
import { parseDesktopDeviceMode, writeDesktopDeviceMode } from '@/lib/desktop-handoff/device-cookie';
import { getBrowserHandoffEnvironment } from '@/lib/desktop-handoff/environment';
import { decideOpenInApp, dismissOpenInApp, launchInDesktopApp } from '@/lib/desktop-handoff/open-in-app';
import { useAuth } from '@/providers/auth-provider';
type PromptState = {
  kind: 'none';
} | Extract<OpenInAppDecision, {
  kind: 'offer';
}> | {
  kind: 'opened';
  routeFamily: DesktopHandoffRouteFamily;
  alwaysOpen: boolean;
};

/** The offer arrives after the page rather than with it. */
const OFFER_DELAY_MS = 1000;
const OFFER_COPY = 'Review locally with the cubic desktop app';

/**
 * The offer: one line at the bottom centre, clear of the PR page's chat composer in the bottom-left
 * corner. Whether to always open links in the app is asked after the app opens, once the handoff
 * has worked.
 */
function OpenInAppOffer(t0) {
  const $ = _c(14);
  const {
    routeFamily,
    onOpen,
    onDismiss
  } = t0;
  const posthog = usePostHog();
  const [shown, setShown] = useState(false);
  let t1;
  let t2;
  if ($[0] !== posthog || $[1] !== routeFamily) {
    t1 = () => {
      const timeout = window.setTimeout(() => {
        setShown(true);
        posthog.capture("desktop_open_in_app_offered", {
          route_family: routeFamily
        });
      }, OFFER_DELAY_MS);
      return () => window.clearTimeout(timeout);
    };
    t2 = [posthog, routeFamily];
    $[0] = posthog;
    $[1] = routeFamily;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!shown) {
    return null;
  }
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span aria-hidden="true" className="size-4 shrink-0 rounded-[2px] bg-foreground" />;
    t4 = <span className="whitespace-nowrap text-sm">{OFFER_COPY}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] !== onOpen) {
    t5 = <Button size="sm" className="px-3.5" onClick={onOpen}>Open</Button>;
    $[6] = onOpen;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <X className="size-4" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== onDismiss) {
    t7 = <Button size="icon" variant="ghost" className="size-8" aria-label="Not now" onClick={onDismiss}>{t6}</Button>;
    $[9] = onDismiss;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div role="dialog" aria-label="Open in the cubic desktop app" className="fixed bottom-6 left-1/2 z-[90] flex -translate-x-1/2 items-center gap-3 rounded-lg border bg-popover py-2 pl-3 pr-2 shadow-xl animate-in fade-in slide-in-from-bottom-2">{t3}{t4}{t5}{t7}</div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}

/**
 * Offers to open the PR or inbox page a desktop user landed on in the browser (usually from a
 * Slack, GitHub or email link) in the cubic desktop app, signed in or not. Once it opens, the
 * browser shows a popup where "Always open links in the app" is turned on or off. With it on, the
 * proxy opens those pages in the app before they render; the offer never opens the app unasked.
 */
export function DesktopOpenPrompt() {
  const $ = _c(36);
  const pathname = usePathname();
  const posthog = usePostHog();
  const {
    isDesktopApp
  } = useToDesktop();
  const {
    isAuthenticated,
    isLoading: isAuthLoading
  } = useAuth();
  const {
    data: profile,
    isError: isProfileError
  } = useProfile();
  const [initialUrl] = useState(_temp);
  const [deviceMode] = useState(_temp2);
  const [neverOffers] = useState(_temp3);
  const decidedRef = useRef(false);
  const [hasDecided, setHasDecided] = useState(false);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      kind: "none"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  const desktopAppLastSeenAt = profile?.desktopAppLastSeenAt ?? null;
  const canDecide = Boolean(profile) || !isAuthLoading && !isAuthenticated;
  let t1;
  let t2;
  if ($[1] !== canDecide || $[2] !== desktopAppLastSeenAt || $[3] !== deviceMode || $[4] !== initialUrl || $[5] !== isDesktopApp || $[6] !== neverOffers) {
    t1 = () => {
      if (decidedRef.current || isDesktopApp || neverOffers || !initialUrl || !canDecide) {
        return;
      }
      const environment_0 = getBrowserHandoffEnvironment();
      const decide = () => {
        decidedRef.current = true;
        setHasDecided(true);
        const decision = decideOpenInApp({
          url: initialUrl,
          deviceMode,
          desktopAppLastSeenAt,
          now: Date.now()
        });
        if (decision.kind === "offer") {
          setState(decision);
        }
      };
      if (environment_0.isPrerendering) {
        document.addEventListener("prerenderingchange", decide, {
          once: true
        });
        return () => document.removeEventListener("prerenderingchange", decide);
      }
      decide();
    };
    t2 = [canDecide, desktopAppLastSeenAt, deviceMode, initialUrl, isDesktopApp, neverOffers];
    $[1] = canDecide;
    $[2] = desktopAppLastSeenAt;
    $[3] = deviceMode;
    $[4] = initialUrl;
    $[5] = isDesktopApp;
    $[6] = neverOffers;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  const isDeciding = !isDesktopApp && !neverOffers && !isProfileError && !hasDecided;
  let t3;
  if ($[9] !== initialUrl || $[10] !== pathname || $[11] !== state.kind) {
    t3 = state.kind === "offer" && initialUrl !== null && pathname === new URL(initialUrl).pathname;
    $[9] = initialUrl;
    $[10] = pathname;
    $[11] = state.kind;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const isOfferPage = t3;
  const isShowing = state.kind === "opened" || isOfferPage;
  let t4;
  let t5;
  if ($[13] !== isDeciding || $[14] !== isShowing) {
    t4 = () => {
      useDesktopOpenPromptStore.setState({
        isDeciding,
        isShowing
      });
      return _temp4;
    };
    t5 = [isDeciding, isShowing];
    $[13] = isDeciding;
    $[14] = isShowing;
    $[15] = t4;
    $[16] = t5;
  } else {
    t4 = $[15];
    t5 = $[16];
  }
  useEffect(t4, t5);
  if (state.kind === "opened") {
    let t6;
    if ($[17] !== posthog || $[18] !== state) {
      t6 = alwaysOpen => {
        writeDesktopDeviceMode(alwaysOpen ? "always" : "ask");
        posthog.capture("desktop_open_in_app_always_changed", {
          route_family: state.routeFamily,
          always_open: alwaysOpen
        });
        setState({
          ...state,
          alwaysOpen
        });
      };
      $[17] = posthog;
      $[18] = state;
      $[19] = t6;
    } else {
      t6 = $[19];
    }
    const changeAlwaysOpen = t6;
    let t7;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setState({
        kind: "none"
      });
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== changeAlwaysOpen || $[22] !== state.alwaysOpen) {
      t8 = <HandoffInterstitial alwaysOpen={state.alwaysOpen} onAlwaysOpenChange={changeAlwaysOpen} onContinue={t7} />;
      $[21] = changeAlwaysOpen;
      $[22] = state.alwaysOpen;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    return t8;
  }
  if (state.kind !== "offer") {
    return null;
  }
  if (!isOfferPage) {
    return null;
  }
  let t6;
  if ($[24] !== deviceMode || $[25] !== posthog || $[26] !== state.routeFamily || $[27] !== state.target) {
    t6 = () => {
      posthog.capture("desktop_open_in_app", {
        route_family: state.routeFamily,
        automatic: false
      });
      if (deviceMode === null) {
        writeDesktopDeviceMode("ask");
      }
      launchInDesktopApp(state.target);
      setState({
        kind: "opened",
        routeFamily: state.routeFamily,
        alwaysOpen: deviceMode === "always"
      });
    };
    $[24] = deviceMode;
    $[25] = posthog;
    $[26] = state.routeFamily;
    $[27] = state.target;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const openInApp = t6;
  let t7;
  if ($[29] !== posthog || $[30] !== state.routeFamily) {
    t7 = () => {
      dismissOpenInApp();
      posthog.capture("desktop_open_in_app_dismissed", {
        route_family: state.routeFamily
      });
      setState({
        kind: "none"
      });
    };
    $[29] = posthog;
    $[30] = state.routeFamily;
    $[31] = t7;
  } else {
    t7 = $[31];
  }
  const notNow = t7;
  let t8;
  if ($[32] !== notNow || $[33] !== openInApp || $[34] !== state.routeFamily) {
    t8 = <OpenInAppOffer routeFamily={state.routeFamily} onOpen={openInApp} onDismiss={notNow} />;
    $[32] = notNow;
    $[33] = openInApp;
    $[34] = state.routeFamily;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  return t8;
}
function _temp4() {
  return useDesktopOpenPromptStore.setState({
    isDeciding: true,
    isShowing: false
  });
}
function _temp3() {
  if (typeof window === "undefined") {
    return false;
  }
  const environment = getBrowserHandoffEnvironment();
  return environment.isMobile || environment.isIframe;
}
function _temp2() {
  return typeof document === "undefined" ? null : parseDesktopDeviceMode(document.cookie);
}
function _temp() {
  return typeof window === "undefined" ? null : window.location.href;
}